import { keyBy } from 'lodash';
import { RestaurantBranch } from 'lib/types/global';
import { groupByList } from 'lib/common/utils/groupByList';
import { Menu, Category, Item, Inventory } from './types';

type ApiInventory = {
  menus: ApiMenu[];
  categories: Category[];
  items: ApiItem[];
};

// input: branch IDs -> output: full branches
type ApiMenu = Omit<Menu, 'branches'> & {
  branches: number[];
};

type ApiItem = Omit<Item, 'menu' | 'categories'>;

const hydrateItem = (
  categoryIdToMenus: Partial<Record<number, Menu[]>>,
  itemIdToCategories: Partial<Record<number, Category[]>>,
  item: ApiItem,
): Item => {
  const categories = itemIdToCategories[item.id] || [];
  const [firstCategory] = categories;
  return {
    ...item,
    categories,
    // there should only be a single menu per item.
    menu: (firstCategory && categoryIdToMenus[firstCategory.id]?.[0]) || {},
  };
};

const hydrateMenuBranches = (
  idToBranches: Partial<Record<string, RestaurantBranch>>,
  { branches, ...menu }: ApiMenu,
): Menu => ({
  ...menu,
  branches: branches.map((id) => idToBranches[id]),
});

export const hydrateInventory = (
  { menus, categories, items }: ApiInventory,
  branches: RestaurantBranch[],
): Inventory => {
  const idToBranches = keyBy(branches, 'id');
  const hydratedMenus = menus.map((menu) =>
    hydrateMenuBranches(idToBranches, menu),
  );

  const idsExtractor = (element: Menu | Category) =>
    'categoryIds' in element ? element.categoryIds : element.itemIds;
  const categoryIdToMenus = groupByList(hydratedMenus, idsExtractor);
  const itemIdToCategories = groupByList(categories, idsExtractor);

  return {
    menus: hydratedMenus,
    categories,
    items: items.map((item) =>
      hydrateItem(categoryIdToMenus, itemIdToCategories, item),
    ),
  };
};
